/**
 * TodayDJView — "Vista Hoy DJ"
 *
 * Pantalla mobile-first que muestra los eventos del DÍA ACTUAL para que el DJ
 * los vea de un vistazo. Maneja múltiples eventos el mismo día (lista vertical)
 * y, si no hay eventos hoy, muestra el PRÓXIMO evento futuro como estado vacío.
 *
 * Cada tarjeta muestra:
 *  - Nombre (novios/evento), horario, ubicación, DJ asignado
 *  - Progreso de la lista personalizada (played/total + %) cuando corresponde
 *  - CTA "Abrir evento" que entra al detalle + activa Modo En Vivo
 *
 * Feature solicitada en Feb 2026 (P0) tras las mejoras de Modo En Vivo.
 * Presentacional puro: recibe los datos y callbacks por props.
 */

import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Calendar, Clock, MapPin, Headphones, PlayCircle, ListMusic } from 'lucide-react';
import { EventSpacesDisplay } from './EventSpaces';

// Devuelve "YYYY-MM-DD" local (misma semántica que usa la app para fecha_evento)
const todayIsoLocal = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const onlyDate = (v) => (v ? String(v).split('T')[0] : '');

const formatHumanDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(`${onlyDate(iso)}T00:00:00`);
    return d.toLocaleDateString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  } catch {
    return iso;
  }
};

const AssignmentCard = ({ assignment, onOpen, highlight = false, badgeLabel }) => {
  const total = assignment.custom_list_songs || 0;
  const played = assignment.custom_list_played_count || 0;
  const percent = total > 0 ? Math.round((played / total) * 100) : 0;
  const hasProgress = played > 0 && total > 0;

  return (
    <Card
      className={`bg-slate-800 border-slate-700 ${highlight ? 'ring-2 ring-red-500/60 shadow-lg shadow-red-900/20' : ''}`}
      data-testid={`today-dj-card-${assignment.id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-white truncate">
              {assignment.nombre_novios || 'Evento sin nombre'}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {badgeLabel && (
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-900/40 text-red-300 font-semibold">
                  {badgeLabel}
                </span>
              )}
              {assignment.estado && (
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    assignment.estado === 'completado'
                      ? 'bg-green-900/40 text-green-300'
                      : assignment.estado === 'confirmado'
                      ? 'bg-blue-900/40 text-blue-300'
                      : 'bg-orange-900/40 text-orange-300'
                  }`}
                >
                  {assignment.estado}
                </span>
              )}
              {assignment.my_role && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-900/40 text-purple-300 font-medium">
                  {assignment.my_role}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center gap-2 text-slate-300">
            <Calendar className="w-4 h-4 text-cyan-400 flex-shrink-0" />
            <span className="capitalize">{formatHumanDate(assignment.fecha_evento)}</span>
          </div>
          {(assignment.horario_inicio || assignment.horario_fin) && (
            <div className="flex items-center gap-2 text-slate-300">
              <Clock className="w-4 h-4 text-cyan-400 flex-shrink-0" />
              <span>
                {assignment.horario_inicio || '—'}
                {assignment.horario_fin ? ` - ${assignment.horario_fin}` : ''}
              </span>
            </div>
          )}
          {assignment.lugar_evento && (
            <div className="flex items-center gap-2 text-slate-300">
              <MapPin className="w-4 h-4 text-cyan-400 flex-shrink-0" />
              <span className="truncate">{assignment.lugar_evento}</span>
            </div>
          )}
          {assignment.nombre_dj && (
            <div className="flex items-center gap-2 text-slate-300">
              <Headphones className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span className="truncate">DJ: {assignment.nombre_dj}</span>
            </div>
          )}
          {assignment.espacios_evento && assignment.espacios_evento.length > 0 && (
            <div className="pt-1">
              <EventSpacesDisplay espacios={assignment.espacios_evento} compact />
            </div>
          )}
        </div>

        {total > 0 && (
          <div className="pt-2 border-t border-slate-700" data-testid={`today-dj-progress-${assignment.id}`}>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="flex items-center gap-1.5 text-slate-400">
                <ListMusic className="w-3.5 h-3.5" />
                Lista personalizada
              </span>
              <span className="text-white font-semibold tabular-nums">
                {played}<span className="text-slate-500">/{total}</span>
                {hasProgress && (
                  <span className="ml-2 text-emerald-400">{percent}%</span>
                )}
              </span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-slate-700 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        )}

        <Button
          onClick={() => onOpen(assignment)}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
          data-testid={`today-dj-open-btn-${assignment.id}`}
        >
          <PlayCircle className="w-4 h-4 mr-2" />
          Abrir evento
        </Button>
      </CardContent>
    </Card>
  );
};

export const TodayDJView = ({ assignments = [], onOpenEvent }) => {
  const { todayEvents, nextUpcoming } = useMemo(() => {
    const today = todayIsoLocal();
    const withDate = assignments.filter((a) => !!a.fecha_evento);
    const todayEvents = withDate.filter((a) => onlyDate(a.fecha_evento) === today);

    // Sort today's events by start time (if any)
    todayEvents.sort((a, b) => {
      const ta = a.horario_inicio || '99:99';
      const tb = b.horario_inicio || '99:99';
      return ta.localeCompare(tb);
    });

    // Next future event for empty-state
    let nextUpcoming = null;
    if (todayEvents.length === 0) {
      const future = withDate
        .filter((a) => onlyDate(a.fecha_evento) > today)
        .sort((a, b) => onlyDate(a.fecha_evento).localeCompare(onlyDate(b.fecha_evento)));
      nextUpcoming = future[0] || null;
    }

    return { todayEvents, nextUpcoming };
  }, [assignments]);

  const hasToday = todayEvents.length > 0;

  return (
    <section
      className="bg-gradient-to-br from-slate-900 via-slate-900 to-red-950/20 border border-slate-800 rounded-xl p-4 sm:p-5"
      data-testid="today-dj-view"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <h2 className="text-lg sm:text-xl font-bold text-white" data-testid="today-dj-heading">
            {hasToday ? 'Hoy' : 'Próximamente'}
          </h2>
          {hasToday && (
            <span
              className="text-xs px-2 py-0.5 rounded-full bg-red-900/40 text-red-300 font-semibold"
              data-testid="today-dj-count"
            >
              {todayEvents.length} {todayEvents.length === 1 ? 'evento' : 'eventos'}
            </span>
          )}
        </div>
      </div>

      {hasToday ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="today-dj-events-list">
          {todayEvents.map((a) => (
            <AssignmentCard
              key={a.id}
              assignment={a}
              onOpen={onOpenEvent}
              highlight
              badgeLabel="Hoy"
            />
          ))}
        </div>
      ) : nextUpcoming ? (
        <div data-testid="today-dj-empty-with-next">
          <p className="text-sm text-slate-400 mb-3">
            No tienes eventos hoy. Tu próximo evento:
          </p>
          <AssignmentCard
            assignment={nextUpcoming}
            onOpen={onOpenEvent}
            badgeLabel="Próximo"
          />
        </div>
      ) : (
        <div className="py-6 text-center" data-testid="today-dj-empty">
          <p className="text-slate-400 mb-1">No tienes eventos hoy.</p>
          <p className="text-slate-500 text-sm">
            Tampoco hay eventos futuros asignados. Contacta con tu administrador.
          </p>
        </div>
      )}
    </section>
  );
};

export default TodayDJView;
